// src/utils/centerfyTracking.js
import { API_BASE_URL } from "../config/api.config";

// Session ID storage key
const SESSION_ID_KEY = "centerfy_session_id";

// Tracking configuration cache
let trackingConfig = null;
let configLoaded = false;

/**
 * Generate or retrieve session ID
 */
export const getSessionId = () => {
  try {
    let sessionId = localStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      // Generate a unique session ID
      sessionId = `session_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      localStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch (error) {
    console.error("Error getting session ID:", error);
    // Fallback to in-memory session ID if localStorage fails
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

/**
 * Get current user ID from auth context or localStorage
 */
export const getUserId = () => {
  try {
    const user = localStorage.getItem("bookatherapy_user");
    if (user) {
      const userData = JSON.parse(user);
      return userData?.id || userData?._id || null;
    }
    return null;
  } catch (error) {
    console.error("Error getting user ID:", error);
    return null;
  }
};

/**
 * Get current user email from localStorage
 */
export const getUserEmail = () => {
  try {
    const user = localStorage.getItem("bookatherapy_user");
    if (user) {
      const userData = JSON.parse(user);
      return userData?.email || null;
    }
    return null;
  } catch (error) {
    console.error("Error getting user email:", error);
    return null;
  }
};

/**
 * Get current user name from localStorage
 */
export const getUserName = () => {
  try {
    const user = localStorage.getItem("bookatherapy_user");
    if (user) {
      const userData = JSON.parse(user);
      return userData?.fullName || userData?.name || null;
    }
    return null;
  } catch (error) {
    console.error("Error getting user name:", error);
    return null;
  }
};

/**
 * Get current user phone from localStorage
 */
export const getUserPhone = () => {
  try {
    const user = localStorage.getItem("bookatherapy_user");
    if (user) {
      const userData = JSON.parse(user);
      return userData?.phone || userData?.phoneNumber || null;
    }
    return null;
  } catch (error) {
    console.error("Error getting user phone:", error);
    return null;
  }
};

/**
 * Extract UTM parameters from URL
 */
export const getUTMParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {};

    const utmKeys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
    ];

    utmKeys.forEach((key) => {
      const value = params.get(key);
      if (value) {
        utmParams[key] = value;
      }
    });

    return Object.keys(utmParams).length > 0 ? utmParams : null;
  } catch (error) {
    console.error("Error getting UTM params:", error);
    return null;
  }
};

/**
 * Fetch tracking configuration from backend
 */
export const fetchTrackingConfig = async () => {
  if (configLoaded && trackingConfig) {
    return trackingConfig;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/centerfy/config`, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      console.warn("Failed to fetch tracking config:", response.status);
      return null;
    }

    const data = await response.json();
    trackingConfig = data?.config || [];
    configLoaded = true;
    return trackingConfig;
  } catch (error) {
    console.error("Error fetching tracking config:", error);
    return null;
  }
};

/**
 * Check if a page should be tracked
 */
export const shouldTrackPage = (pagePath, config) => {
  if (!config || !Array.isArray(config)) {
    return false;
  }

  // Normalize page path (remove query params, trailing slashes)
  const normalizedPath = pagePath.split("?")[0].replace(/\/$/, "") || "/";

  const pageConfig = config.find((page) => {
    const configPath = page.pagePath.replace(/\/$/, "") || "/";
    return configPath === normalizedPath;
  });

  return pageConfig && pageConfig.trackingRules?.length > 0;
};

/**
 * Check if an event type is enabled for a page
 */
export const isEventTypeEnabled = (pagePath, eventType, config) => {
  if (!config || !Array.isArray(config)) {
    return false;
  }

  const normalizedPath = pagePath.split("?")[0].replace(/\/$/, "") || "/";
  const pageConfig = config.find((page) => {
    const configPath = page.pagePath.replace(/\/$/, "") || "/";
    return configPath === normalizedPath;
  });

  if (!pageConfig) {
    return false;
  }

  return pageConfig.trackingRules?.some(
    (rule) => rule.eventType === eventType && rule.enabled,
  );
};

/**
 * Initialize tracking
 * Fetches configuration and sets up event listeners
 */
export const initTracking = async () => {
  try {
    const config = await fetchTrackingConfig();
    if (!config) {
      console.log("Centerfy tracking not configured or disabled");
      return false;
    }

    // Set up automatic click tracking for enabled pages
    if (typeof window !== "undefined") {
      document.addEventListener("click", (event) => {
        const pagePath = window.location.pathname;
        if (isEventTypeEnabled(pagePath, "click", config)) {
          const target = event.target;
          const elementId = target.id || target.className || target.tagName;
          trackClick(elementId, {
            text: target.textContent?.substring(0, 100),
            tag: target.tagName,
          });
        }
      });
    }

    return true;
  } catch (error) {
    console.error("Error initializing tracking:", error);
    return false;
  }
};

/**
 * Track page view
 */
export const trackPageView = async (pagePath, metadata = {}) => {
  try {
    const config = await fetchTrackingConfig();
    if (!shouldTrackPage(pagePath, config)) {
      return;
    }

    if (!isEventTypeEnabled(pagePath, "pageview", config)) {
      return;
    }

    const userId = getUserId();
    const sessionId = getSessionId();
    const userEmail = getUserEmail();
    const userName = getUserName();
    const userPhone = getUserPhone(); // Get phone number
    const utmParams = getUTMParams();

    const response = await fetch(`${API_BASE_URL}/centerfy/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        eventType: "pageview",
        pagePath,
        userId,
        sessionId,
        userEmail,
        userName,
        userPhone, // Add phone number
        eventData: {
          ...metadata,
          url: window.location.href,
          referrer: document.referrer,
          utmParams: utmParams, // Add UTM parameters
          timestamp: new Date().toISOString(),
        },
      }),
    });

    if (!response.ok) {
      console.warn("Failed to track page view:", response.status);
    }
  } catch (error) {
    console.error("Error tracking page view:", error);
  }
};

/**
 * Track custom event
 */
export const trackEvent = async (
  eventType,
  eventData = {},
  pagePath = null,
) => {
  try {
    const path = pagePath || window.location.pathname;
    const config = await fetchTrackingConfig();

    if (!isEventTypeEnabled(path, eventType, config)) {
      return;
    }

    const userId = getUserId();
    const sessionId = getSessionId();
    const userEmail = getUserEmail();
    const userName = getUserName();
    const userPhone = getUserPhone(); // Get phone number

    const response = await fetch(`${API_BASE_URL}/centerfy/track`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        eventType,
        pagePath: path,
        userId,
        sessionId,
        userEmail,
        userName,
        userPhone, // Add phone number
        eventData: {
          ...eventData,
          timestamp: new Date().toISOString(),
        },
      }),
    });

    if (!response.ok) {
      console.warn(`Failed to track event ${eventType}:`, response.status);
    }
  } catch (error) {
    console.error(`Error tracking event ${eventType}:`, error);
  }
};

/**
 * Track button/link click
 */
export const trackClick = async (elementId, metadata = {}) => {
  try {
    const pagePath = window.location.pathname;
    const config = await fetchTrackingConfig();

    if (!isEventTypeEnabled(pagePath, "click", config)) {
      return;
    }

    await trackEvent(
      "click",
      {
        elementId,
        ...metadata,
      },
      pagePath,
    );
  } catch (error) {
    console.error("Error tracking click:", error);
  }
};

/**
 * Track form submission
 */
export const trackFormSubmit = async (formId, formData = {}) => {
  try {
    const pagePath = window.location.pathname;
    const config = await fetchTrackingConfig();

    if (!isEventTypeEnabled(pagePath, "formsubmit", config)) {
      return;
    }

    // Sanitize sensitive data
    const sanitizedData = { ...formData };
    const sensitiveFields = [
      "password",
      "passwordConfirm",
      "cardNumber",
      "cvv",
      "ssn",
    ];
    sensitiveFields.forEach((field) => {
      if (sanitizedData[field]) {
        sanitizedData[field] = "[REDACTED]";
      }
    });

    await trackEvent(
      "formsubmit",
      {
        formId,
        formData: sanitizedData,
      },
      pagePath,
    );
  } catch (error) {
    console.error("Error tracking form submission:", error);
  }
};

/**
 * Link session to user when user logs in
 * Should be called after successful authentication
 */
export const linkSessionToUser = async (userId) => {
  try {
    // This will be handled automatically by the backend when tracking events
    // with both userId and sessionId, but we can explicitly call it if needed
    const sessionId = getSessionId();
    if (sessionId && userId) {
      // The backend will handle linking when it receives events with both IDs
      console.log("Session will be linked to user on next tracking event");
    }
  } catch (error) {
    console.error("Error linking session to user:", error);
  }
};
